import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Feather } from '@expo/vector-icons';

type Msg = { role: 'user' | 'assistant'; content: string };

const CHAT_API = 'https://homepage.medilogis.net/api/chat';

export default function ChatBot({ navRef }: { navRef?: any }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    {
      role: 'assistant',
      content: '안녕하세요! (주)메디로지스 챗봇입니다. 무엇을 도와드릴까요?',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const { width: winW } = useWindowDimensions();
  const isMobile = winW < 480;
  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 50);
  }, [messages, loading, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    const next = [...messages, { role: 'user' as const, content: msg }];
    setMessages(next);
    setLoading(true);
    try {
      const res = await fetch(CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history: next.slice(-10) }),
      });
      const data = await res.json();
      setMessages([
        ...next,
        { role: 'assistant', content: data.answer || '응답을 받지 못했습니다.' },
      ]);
    } catch {
      setMessages([
        ...next,
        { role: 'assistant', content: '챗봇 서비스에 연결할 수 없습니다.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <TouchableOpacity
        style={[styles.fab, isMobile && styles.fabMobile]}
        onPress={() => setOpen(true)}
        activeOpacity={0.85}
      >
        <Feather name="message-circle" size={isMobile ? 22 : 26} color="#fff" />
      </TouchableOpacity>
    );
  }

  const goInquiry = () => {
    if (navRef?.isReady && navRef.isReady()) {
      try {
        navRef.navigate({
          name: 'Main',
          params: { screen: 'CustomerInquiry' },
          merge: true,
        } as any);
      } catch {
        try { navRef.navigate('CustomerInquiry' as never); } catch {}
      }
      setOpen(false);
    }
  };

  return (
    <View style={[styles.window, isMobile && styles.windowMobile]}>
      <View style={styles.header}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={styles.dot} />
          <Text style={styles.headerTitle}>메디로지스 챗봇</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <TouchableOpacity
            onPress={goInquiry}
            style={styles.inquiryBtn}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          >
            <Feather name="user" size={12} color="#fff" style={{ marginRight: 4 }} />
            <Text style={styles.inquiryBtnText}>직접 문의</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setOpen(false)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Feather name="x" size={22} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        style={styles.body}
        contentContainerStyle={{ padding: 12 }}
      >
        {messages.map((m, i) => (
          <View
            key={i}
            style={[
              styles.bubble,
              m.role === 'user' ? styles.bubbleUser : styles.bubbleBot,
            ]}
          >
            <Text
              style={m.role === 'user' ? styles.bubbleUserText : styles.bubbleBotText}
              selectable
            >
              {m.content}
            </Text>
          </View>
        ))}
        {loading && (
          <View style={[styles.bubble, styles.bubbleBot, { flexDirection: 'row', alignItems: 'center' }]}>
            <ActivityIndicator size="small" color="#304156" />
            <Text style={[styles.bubbleBotText, { marginLeft: 8 }]}>답변 생성 중…</Text>
          </View>
        )}
      </ScrollView>

      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          value={input}
          onChangeText={setInput}
          placeholder="메시지를 입력하세요"
          placeholderTextColor="#9aa0a6"
          onSubmitEditing={send}
          editable={!loading}
          blurOnSubmit={false}
        />
        <TouchableOpacity
          style={[styles.sendBtn, (loading || !input.trim()) && { opacity: 0.5 }]}
          onPress={send}
          disabled={loading || !input.trim()}
        >
          <Feather name="send" size={18} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  fab: {
    position: 'fixed' as any,
    right: 24,
    bottom: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#1890FF',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
    zIndex: 9999,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  window: {
    position: 'fixed' as any,
    right: 24,
    bottom: 24,
    width: 360,
    height: 520,
    maxWidth: '95%' as any,
    maxHeight: '85%' as any,
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 10,
    zIndex: 9999,
  },
  fabMobile: {
    width: 46,
    height: 46,
    borderRadius: 23,
    right: 14,
    bottom: 14,
  },
  windowMobile: {
    width: '92%' as any,
    height: '64%' as any,
    right: 12,
    bottom: 12,
  },
  header: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#304156',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: { color: '#fff', fontSize: 16, fontWeight: '600' },
  dot: {
    width: 8, height: 8, borderRadius: 4, backgroundColor: '#4ade80', marginRight: 8,
  },
  body: { flex: 1, backgroundColor: '#f5f7fa' },
  bubble: {
    maxWidth: '85%',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    marginBottom: 8,
  },
  bubbleUser: {
    alignSelf: 'flex-end',
    backgroundColor: '#1890FF',
    borderBottomRightRadius: 4,
  },
  bubbleBot: {
    alignSelf: 'flex-start',
    backgroundColor: '#fff',
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  bubbleUserText: { color: '#fff', fontSize: 14, lineHeight: 20 },
  bubbleBotText: { color: '#111', fontSize: 14, lineHeight: 20 },
  inputRow: {
    flexDirection: 'row',
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    height: 40,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 20,
    fontSize: 14,
    color: '#111',
    backgroundColor: '#fff',
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  sendBtn: {
    marginLeft: 8,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#1890FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  inquiryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.15)',
    marginRight: 4,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  inquiryBtnText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
});
